// src/components/family/EmptyChildrenState.tsx
// Empty state illustration when no children exist

import { UserPlus, Heart, Users } from 'lucide-react';

interface EmptyChildrenStateProps {
  onAddChild?: () => void;
  isFiltered?: boolean;
}

export function EmptyChildrenState({ onAddChild, isFiltered = false }: EmptyChildrenStateProps) {
  if (isFiltered) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        {/* Illustration */}
        <div className="relative mb-8">
          <div className="w-32 h-32 rounded-full bg-cream-dark flex items-center justify-center">
            <Users className="w-16 h-16 text-warm-gray/40" />
          </div>
          {/* Decorative elements */}
          <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-sage/10 flex items-center justify-center">
            <span className="text-sage text-lg">?</span>
          </div>
        </div>

        {/* Text */}
        <h3 className="font-display text-xl font-semibold text-navy mb-2 text-center">
          No children match your filters
        </h3>
        <p className="text-warm-gray text-center max-w-sm mb-6">
          Try adjusting your search or filters to find what you're looking for.
        </p>

        {/* Action */}
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2.5 rounded-xl bg-cream hover:bg-cream-dark text-navy font-medium transition-colors"
        >
          Clear Filters
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      {/* Illustration */}
      <div className="relative mb-8">
        {/* Main circle */}
        <div className="w-40 h-40 rounded-full bg-gradient-to-br from-sage/10 to-terracotta/10 flex items-center justify-center relative overflow-hidden">
          {/* Inner decoration */}
          <div className="absolute inset-4 rounded-full border-2 border-dashed border-sage/20" />
          
          {/* Icon */}
          <div className="relative z-10 w-20 h-20 rounded-full bg-white shadow-soft flex items-center justify-center">
            <Heart className="w-10 h-10 text-sage" />
          </div>
        </div>

        {/* Floating decorative elements */}
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-terracotta/10 flex items-center justify-center animate-float" style={{ animationDelay: '0s' }}>
          <span className="text-terracotta text-lg">✦</span>
        </div>
        <div className="absolute top-1/2 -left-6 w-8 h-8 rounded-full bg-sage/10 flex items-center justify-center animate-float" style={{ animationDelay: '0.5s' }}>
          <span className="text-sage text-sm">✦</span>
        </div>
        <div className="absolute top-1/2 -right-6 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center animate-float" style={{ animationDelay: '1s' }}>
          <span className="text-blue-500 text-sm">✦</span>
        </div>
      </div>

      {/* Text */}
      <h3 className="font-display text-2xl font-semibold text-navy mb-3 text-center">
        Add your first child
      </h3>
      <p className="text-warm-gray text-center max-w-md mb-8 leading-relaxed">
        Start by adding your child's profile. Once added, you'll be able to track their
        health, manage documents, schedule appointments, and access personalized resources.
      </p>

      {/* Features preview */}
      <div className="flex flex-wrap justify-center gap-4 mb-8">
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-sage/10 text-sage-dark text-sm">
          <Heart className="w-4 h-4" />
          <span>Health Tracking</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-terracotta/10 text-terracotta text-sm">
          <Users className="w-4 h-4" />
          <span>Care Team</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-navy/10 text-navy text-sm">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <span>Documents</span>
        </div>
      </div>

      {/* Action */}
      <button
        onClick={onAddChild}
        className="group flex items-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r from-sage to-sage-dark text-white font-semibold shadow-lg shadow-sage/30 hover:shadow-xl hover:shadow-sage/40 hover:-translate-y-1 transition-all duration-300"
      >
        <UserPlus className="w-5 h-5 group-hover:scale-110 transition-transform" />
        Add Child Profile
      </button>

      {/* Subtle note */}
      <p className="mt-6 text-sm text-warm-gray/70 text-center max-w-sm">
        Your child's information is stored securely and only visible to family members you authorize.
      </p>

      {/* Animation keyframes */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default EmptyChildrenState;
